"use client";

import { Button } from "@package/subframe/components/Button";
import { DropdownMenu } from "@package/subframe/components/DropdownMenu";
import { IconButton } from "@package/subframe/components/IconButton";
import { IconWithLogoBackground } from "@package/subframe/components/IconWithLogoBackground";
import { Table } from "@package/subframe/components/Table";
import {
  FeatherArchive,
  FeatherBuilding,
  FeatherMoreHorizontal,
  FeatherPauseCircle,
  FeatherPencil,
  FeatherPlayCircle,
  FeatherPlus,
} from "@subframe/core";
import * as SubframeCore from "@subframe/core";
import { useNavigate } from "@tanstack/react-router";
import { useMemo, useState } from "react";

import { WorkspaceStatusBadge } from "@/components/checklists/WorkspaceStatusBadge";
import { Pagination } from "@/components/ui/pagination";
import { Spinner } from "@/components/ui/spinner";
import { TABLE_MIN_WIDTH } from "@/components/ui/table-scroll-container";
import { useAuth } from "@/contexts/AuthContext";
import { useOrganisation } from "@/contexts/OrganisationContext";
import type { NurseryItemDto } from "@/generated/model";
import {
  ChangeNurseryStatusDtoStatus,
  NurseryItemDtoStatus,
} from "@/generated/model";
import { useListNurseriesByOrganisationQueryControllerList } from "@/generated/nurseries/nurseries";
import { useFindOrganisationQueryControllerFindBySlug } from "@/generated/organisations/organisations";
import { DEFAULT_PAGE_SIZE } from "@/lib/pagination";
import { nurseryStatusPill } from "@/lib/workspace/status-pill";

import { BranchFormDialog } from "./BranchFormDialog";
import { ChangeNurseryStatusDialog } from "./ChangeNurseryStatusDialog";

interface BranchesTableProps {
  organisationSlug: string;
}

export function BranchesTable({ organisationSlug }: BranchesTableProps) {
  const { isPlatformAdmin } = useAuth();
  const { isOrgAdmin } = useOrganisation();
  const canManageNurseries = isPlatformAdmin || isOrgAdmin;
  const navigate = useNavigate();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingBranchId, setEditingBranchId] = useState<string | null>(null);
  const [statusChange, setStatusChange] = useState<{
    nursery: NurseryItemDto;
    target: ChangeNurseryStatusDtoStatus;
  } | null>(null);

  // Fetch organisation to get its ID
  const { data: organisationData, isLoading: orgLoading } =
    useFindOrganisationQueryControllerFindBySlug(organisationSlug);

  const organisationId = organisationData?.data.id;

  const handleCreateClick = () => {
    setEditingBranchId(null);
    setDialogOpen(true);
  };

  const handleEditClick = (branchId: string) => {
    setEditingBranchId(branchId);
    setDialogOpen(true);
  };

  const handleDialogClose = (open: boolean) => {
    setDialogOpen(open);
    if (!open) {
      setEditingBranchId(null);
    }
  };

  // Fetch branches for this organisation
  const {
    data: branchesData,
    isLoading: branchesLoading,
    error: branchesError,
  } = useListNurseriesByOrganisationQueryControllerList(organisationId ?? "", {
    query: {
      enabled: !!organisationId,
    },
  });

  const branches = useMemo(() => branchesData?.data.data ?? [], [branchesData]);
  const [page, setPage] = useState(1); // 1-based, matches the Pagination component
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  // The endpoint returns the full set, so the page is sliced client-side.
  const pagedBranches = useMemo(
    () => branches.slice((page - 1) * pageSize, page * pageSize),
    [branches, page, pageSize],
  );
  const isLoading = orgLoading || branchesLoading;

  return (
    <div className="flex w-full flex-col items-start gap-6 bg-white animate-fade-in">
      {/* Header */}
      <div className="flex w-full flex-col items-start gap-1">
        <div className="flex w-full flex-wrap items-center gap-4">
          <div className="flex grow shrink-0 basis-0 flex-col items-start gap-1">
            <span className="text-heading-2 font-heading-2 text-default-font">
              Nurseries - {organisationData?.data.name}
            </span>
            <span className="text-body font-body text-subtext-color">
              Manage nurseries for this nursery group
            </span>
          </div>
          {canManageNurseries && (
            <Button
              variant="brand-primary"
              icon={<FeatherPlus />}
              onClick={handleCreateClick}
              disabled={!organisationId}
            >
              Add Nursery
            </Button>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="flex w-full flex-col items-start">
        {isLoading ? (
          <div className="flex w-full items-center justify-center py-12">
            <Spinner className="h-8 w-8 text-primary" />
          </div>
        ) : branchesError ? (
          <div className="flex w-full flex-col items-center justify-center py-12 gap-4">
            <IconWithLogoBackground variant="error" size="x-large" />
            <div className="flex flex-col items-center gap-2">
              <span className="text-heading-3 font-heading-3 text-default-font">
                Error loading nurseries
              </span>
              <span className="text-body font-body text-subtext-color">
                Please try again later.
              </span>
            </div>
          </div>
        ) : branches.length === 0 ? (
          <div className="flex w-full flex-col items-center justify-center py-12 gap-4 rounded-md border border-solid border-secondary-beige-200">
            <IconWithLogoBackground
              variant="neutral"
              size="x-large"
              icon={<FeatherBuilding />}
            />
            <div className="flex flex-col items-center gap-2">
              <span className="text-heading-3 font-heading-3 text-default-font">
                No nurseries found
              </span>
              <span className="text-body font-body text-subtext-color text-center max-w-md">
                Add your first nursery to get started.
              </span>
            </div>
            {canManageNurseries && (
              <Button
                variant="brand-primary"
                icon={<FeatherPlus />}
                onClick={handleCreateClick}
                disabled={!organisationId}
              >
                Add Nursery
              </Button>
            )}
          </div>
        ) : (
          <div className="flex w-full flex-col items-start bg-default-background overflow-auto">
            <Table
              className={TABLE_MIN_WIDTH.md}
              header={
                <Table.HeaderRow>
                  <Table.HeaderCell>Name</Table.HeaderCell>
                  <Table.HeaderCell>Address</Table.HeaderCell>
                  <Table.HeaderCell>Status</Table.HeaderCell>
                  <Table.HeaderCell>Created</Table.HeaderCell>
                  <Table.HeaderCell className="items-center justify-end">
                    Actions
                  </Table.HeaderCell>
                </Table.HeaderRow>
              }
            >
              {pagedBranches.map((branch) => (
                <Table.Row
                  key={branch.id}
                  className="cursor-pointer hover:bg-neutral-50"
                  onClick={() =>
                    navigate({
                      to: "/organisations/$slug/branches/$branchId",
                      params: { slug: organisationSlug, branchId: branch.id },
                    })
                  }
                >
                  <Table.Cell>
                    <span className="text-body-bold font-body-bold text-default-font">
                      {branch.name}
                    </span>
                  </Table.Cell>
                  <Table.Cell>
                    <span className="text-body font-body text-subtext-color">
                      {branch.address.street}, {branch.address.city},{" "}
                      {branch.address.postcode}
                    </span>
                  </Table.Cell>
                  <Table.Cell>
                    <WorkspaceStatusBadge
                      pill={nurseryStatusPill(branch.status)}
                    />
                  </Table.Cell>
                  <Table.Cell>
                    <span className="text-body font-body text-subtext-color">
                      {new Date(branch.createdAt).toLocaleDateString()}
                    </span>
                  </Table.Cell>
                  <Table.Cell variant="aligned-right">
                    {/* Stop clicks here (incl. portaled dropdown items, which
                        bubble through the React tree) from triggering the
                        row's navigate handler. */}
                    <div
                      className="flex items-center justify-end gap-2"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <SubframeCore.DropdownMenu.Root>
                        <SubframeCore.DropdownMenu.Trigger asChild={true}>
                          <IconButton
                            variant="neutral-primary"
                            size="medium"
                            icon={<FeatherMoreHorizontal />}
                            data-testid="nursery-row-actions"
                          />
                        </SubframeCore.DropdownMenu.Trigger>
                        <SubframeCore.DropdownMenu.Portal>
                          <SubframeCore.DropdownMenu.Content
                            side="bottom"
                            align="end"
                            sideOffset={4}
                            asChild={true}
                          >
                            <DropdownMenu>
                              <DropdownMenu.DropdownItem
                                icon={<FeatherPencil />}
                                onClick={() => handleEditClick(branch.id)}
                              >
                                Edit
                              </DropdownMenu.DropdownItem>
                              {isPlatformAdmin &&
                                branch.status ===
                                  NurseryItemDtoStatus.DISABLED && (
                                  <DropdownMenu.DropdownItem
                                    icon={<FeatherPlayCircle />}
                                    onClick={() =>
                                      setStatusChange({
                                        nursery: branch,
                                        target:
                                          ChangeNurseryStatusDtoStatus.ACTIVE,
                                      })
                                    }
                                  >
                                    Enable
                                  </DropdownMenu.DropdownItem>
                                )}
                              {isPlatformAdmin &&
                                branch.status ===
                                  NurseryItemDtoStatus.ACTIVE && (
                                  <DropdownMenu.DropdownItem
                                    icon={<FeatherPauseCircle />}
                                    onClick={() =>
                                      setStatusChange({
                                        nursery: branch,
                                        target:
                                          ChangeNurseryStatusDtoStatus.DISABLED,
                                      })
                                    }
                                  >
                                    Disable
                                  </DropdownMenu.DropdownItem>
                                )}
                              {isPlatformAdmin &&
                                branch.status !==
                                  NurseryItemDtoStatus.ARCHIVED && (
                                  <DropdownMenu.DropdownItem
                                    icon={<FeatherArchive />}
                                    onClick={() =>
                                      setStatusChange({
                                        nursery: branch,
                                        target:
                                          ChangeNurseryStatusDtoStatus.ARCHIVED,
                                      })
                                    }
                                  >
                                    Archive
                                  </DropdownMenu.DropdownItem>
                                )}
                            </DropdownMenu>
                          </SubframeCore.DropdownMenu.Content>
                        </SubframeCore.DropdownMenu.Portal>
                      </SubframeCore.DropdownMenu.Root>
                    </div>
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table>

            <Pagination
              className="pt-4"
              page={page}
              pageSize={pageSize}
              totalCount={branches.length}
              onPageChange={setPage}
              onPageSizeChange={(size) => {
                setPageSize(size);
                setPage(1);
              }}
              itemNoun="nurseries"
            />
          </div>
        )}
      </div>

      {/* Branch Form Dialog (Create/Edit) */}
      {organisationId && (
        <BranchFormDialog
          organisationId={organisationId}
          branchId={editingBranchId}
          open={dialogOpen}
          onOpenChange={handleDialogClose}
        />
      )}

      {/* Change Status Dialog (platform admin) */}
      {statusChange && (
        <ChangeNurseryStatusDialog
          open={true}
          onOpenChange={(open) => {
            if (!open) setStatusChange(null);
          }}
          nursery={statusChange.nursery}
          targetStatus={statusChange.target}
        />
      )}
    </div>
  );
}
